import type {
  Page,
  PageBlocksInput,
  PageTemplate,
  PatchAdminV1StoresStoreIdPagesIdBody,
  PostAdminV1StoresStoreIdPagesBody,
} from '@nucleus-commerce/api-client/admin';

import { emptySeoOverride, fromSeoOverride, toSeoOverride } from '@/features/seo';

import {
  templateBlocks,
  type PageFormInput,
  type PageFormValues,
} from '../schemas/page-form-schema';

export function emptyPageForm(template: PageTemplate = 'default'): PageFormInput {
  return {
    title: '',
    handle: '',
    bodyHtml: '',
    template,
    status: 'draft',
    blocks: {
      faq: [],
      steps: [],
      hero: { enabled: false, heading: '', subheading: '', mediaId: null },
    },
    seo: emptySeoOverride(),
  };
}

export function fromPage(page: Page): PageFormInput {
  const hero = page.blocks?.hero;
  return {
    title: page.title,
    handle: page.handle,
    bodyHtml: page.bodyHtml ?? '',
    template: page.template,
    status: page.status,
    blocks: {
      faq: page.blocks?.faq?.map(({ question, answer }) => ({ question, answer })) ?? [],
      steps: page.blocks?.steps?.map(({ name, text }) => ({ name, text })) ?? [],
      hero: hero
        ? {
            enabled: true,
            heading: hero.heading,
            subheading: hero.subheading ?? '',
            mediaId: hero.mediaId,
          }
        : { enabled: false, heading: '', subheading: '', mediaId: null },
    },
    seo: fromSeoOverride(page.seo),
  };
}

/**
 * Only the blocks the chosen template renders are sent, so switching template drops the others;
 * null when nothing is left, which is what the backend stores for a plain page.
 */
export function toPageBlocks(values: PageFormValues): PageBlocksInput | null {
  const uses = templateBlocks(values.template);
  const blocks: PageBlocksInput = {};
  if (uses.faq && values.blocks.faq.length > 0) blocks.faq = values.blocks.faq;
  if (uses.steps && values.blocks.steps.length > 0) blocks.steps = values.blocks.steps;
  const hero = values.blocks.hero;
  if (hero.enabled) {
    blocks.hero = {
      heading: hero.heading,
      subheading: hero.subheading || null,
      mediaId: hero.mediaId,
    };
  }
  return Object.keys(blocks).length > 0 ? blocks : null;
}

export function toPageCreate(values: PageFormValues): PostAdminV1StoresStoreIdPagesBody {
  const blocks = toPageBlocks(values);
  const seo = toSeoOverride(values.seo);
  return {
    handle: values.handle,
    title: values.title,
    template: values.template,
    status: values.status,
    ...(values.bodyHtml.trim() ? { bodyHtml: values.bodyHtml } : {}),
    ...(blocks ? { blocks } : {}),
    ...(seo ? { seo } : {}),
  };
}

/** Every editable field is sent; cleared text becomes null so the backend clears it too. */
export function toPageUpdate(values: PageFormValues): PatchAdminV1StoresStoreIdPagesIdBody {
  return {
    handle: values.handle,
    title: values.title,
    template: values.template,
    status: values.status,
    bodyHtml: values.bodyHtml.trim() ? values.bodyHtml : null,
    blocks: toPageBlocks(values),
    seo: toSeoOverride(values.seo),
  };
}
